(() => {
  const CARD_SELECTOR = '[data-nivieree-card]';
  const CARD_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000];

  const getLabelTextForInput = (input) => {
    const label = input.nextElementSibling;
    if (!label) return '';
    const srOnly = label.querySelector('.sr-only');
    return (srOnly ? srOnly.textContent : label.textContent).trim();
  };

  const withWidth = (url, width) => {
    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (_e) {
      return url;
    }
  };

  const buildSrcset = (url) => CARD_WIDTHS.map((width) => withWidth(url, width) + ' ' + width + 'w').join(', ');

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* NIVIÈREE · pré-carregamento das fotos de cor (29-10-2026).
     Quando um card com várias cores chega perto da tela, as fotos das outras cores já são
     baixadas em segundo plano, no mesmo tamanho que o card vai usar. Assim a troca de cor
     aparece na hora, em vez de esperar o download no momento do arrasto. */
  const preloadQueue = [];
  let nearObserver = null;

  const getNearObserver = () => {
    if (nearObserver || !('IntersectionObserver' in window)) return nearObserver;
    nearObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        nearObserver.unobserve(entry.target);
        entry.target.__niviereePreloadColors?.();
      });
    }, { rootMargin: '400px 0px 400px 0px' });
    return nearObserver;
  };

  const initCard = (card) => {
    if (!card || card.dataset.niviereeCardReady === 'true') return;
    card.dataset.niviereeCardReady = 'true';

    const track = card.querySelector('[data-nivieree-gallery-track]');
    const viewport = card.querySelector('[data-nivieree-gallery]');
    const slides = Array.from(card.querySelectorAll('[data-nivieree-slide]'));
    const dots = Array.from(card.querySelectorAll('[data-nivieree-dot]'));
    const previousButton = card.querySelector('[data-nivieree-gallery-prev]');
    const nextButton = card.querySelector('[data-nivieree-gallery-next]');
    const quickToggle = card.querySelector('[data-nivieree-quick-toggle]');
    const quickPanel = card.querySelector('[data-nivieree-quick-panel]');
    const quickForm = card.querySelector('.nivieree-quick-add__form');
    const variantInput = card.querySelector('[data-nivieree-variant-input]');
    const variantChoices = Array.from(card.querySelectorAll('[data-nivieree-variant-choice]'));
    const colorInputs = Array.from(card.querySelectorAll('[data-nivieree-color-input], .nivieree-card__swatches input[type="radio"]'));

    let activeIndex = 0;
    let startX = 0;
    let startY = 0;
    let pointerId = null;
    let tracking = false;
    let didSwipe = false;

    const firstImage = slides[0]?.querySelector('img');
    const originalMediaId = String(slides[0]?.dataset.mediaId || '');
    let currentMediaId = originalMediaId;
    let fadeToken = 0;

    if (firstImage) {
      firstImage.dataset.niviereeOriginalSrc = firstImage.getAttribute('src') || firstImage.currentSrc || '';
      firstImage.dataset.niviereeOriginalSrcset = firstImage.getAttribute('srcset') || '';
      firstImage.dataset.niviereeOriginalAlt = firstImage.getAttribute('alt') || '';
    }

    const setIndex = (index) => {
      if (!track || slides.length === 0) return;

      const normalized = (index + slides.length) % slides.length;
      activeIndex = normalized;
      track.style.transform = 'translate3d(' + (-100 * normalized) + '%, 0, 0)';

      dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === normalized ? 'true' : 'false');
      });
    };

    // Volta para a primeira foto sem animar o trilho (sem passar pelas fotos do meio).
    const snapToFirst = () => {
      if (!track || activeIndex === 0) return;
      const previousTransition = track.style.transition;
      track.style.transition = 'none';
      setIndex(0);
      track.getBoundingClientRect();
      track.style.transition = previousTransition;
    };

    /* Com 2 ou mais cores no card, arrastar (ou as setinhas da foto) troca a COR (variante),
       na ordem das bolinhas. Com uma cor só, continua trocando de foto. */
    const swatchInputs = Array.from(card.querySelectorAll('.nivieree-card__swatches input[type="radio"]'))
      .filter((input) => !input.disabled);
    const colorMode = swatchInputs.length > 1;

    const stepColor = (direction) => {
      const current = swatchInputs.findIndex((input) => input.checked);
      const next = swatchInputs[(Math.max(0, current) + direction + swatchInputs.length) % swatchInputs.length];
      if (!next) return;
      next.checked = true;
      next.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const step = (direction) => {
      if (colorMode) stepColor(direction);
      else setIndex(activeIndex + direction);
    };

    // Tamanho fixo (largura real do card) para o navegador escolher sempre o mesmo arquivo
    // no pré-carregamento e na troca: o que foi baixado antes é reaproveitado do cache.
    const getCardSizes = () => {
      const width = Math.round(slides[0]?.getBoundingClientRect().width || 0);
      return width > 0 ? width + 'px' : (firstImage?.getAttribute('sizes') || '');
    };

    const resolveSource = (media) => {
      const mediaId = String(media?.id || '');
      if (!mediaId || !firstImage) return null;

      if (mediaId === originalMediaId) {
        return {
          mediaId,
          src: firstImage.dataset.niviereeOriginalSrc || '',
          srcset: firstImage.dataset.niviereeOriginalSrcset || '',
        };
      }

      const slideImage = slides.find((slide) => String(slide.dataset.mediaId || '') === mediaId)?.querySelector('img');
      if (slideImage && slideImage.getAttribute('srcset')) {
        return {
          mediaId,
          src: slideImage.getAttribute('src') || '',
          srcset: slideImage.getAttribute('srcset') || '',
        };
      }

      const preview = media.preview_image || media.previewImage;
      const url = preview?.src || preview?.url;
      if (!url) return null;
      return { mediaId, src: withWidth(url, 800), srcset: buildSrcset(url) };
    };

    const preloadedKeys = new Set();
    const preloadSource = (source) => {
      if (!source || !source.src) return;
      const key = source.srcset || source.src;
      if (preloadedKeys.has(key)) return;
      preloadedKeys.add(key);

      const image = new Image();
      image.decoding = 'async';
      const sizes = getCardSizes();
      if (sizes) image.sizes = sizes;
      if (source.srcset) image.srcset = source.srcset;
      image.src = source.src;
      preloadQueue.push(image);
      if (preloadQueue.length > 400) preloadQueue.shift();
      if (image.decode) image.decode().catch(() => {});
    };

    const preloadColors = () => {
      swatchInputs.forEach((input) => {
        if (!input.dataset.variantMedia) return;
        try {
          preloadSource(resolveSource(JSON.parse(input.dataset.variantMedia)));
        } catch (_e) {
          // ignore
        }
      });
    };

    if (colorMode && firstImage) {
      card.__niviereePreloadColors = preloadColors;
      const observer = getNearObserver();
      if (observer) observer.observe(card);
      else preloadColors();
      // Garantia extra: no primeiro toque ou passada do mouse, baixa o que ainda faltar.
      card.addEventListener('pointerenter', preloadColors, { once: true, passive: true });
      card.addEventListener('touchstart', preloadColors, { once: true, passive: true });
    }

    /* NIVIÈREE · troca de cor "clean" (29-10-2026).
       Toda troca de cor é igual em todos os produtos: a foto da nova cor aparece com um fade
       rápido no lugar da atual, sem deslizar pelas fotos do meio e sem piscar. */
    const fadeToMedia = (media) => {
      if (!firstImage) return;

      snapToFirst();
      const source = resolveSource(media);
      if (!source || !source.src || source.mediaId === currentMediaId) return;

      const { src, srcset, mediaId } = source;
      currentMediaId = mediaId;
      const token = ++fadeToken;
      const holder = slides[0];
      if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';

      const sizes = getCardSizes();
      const layer = document.createElement('img');
      layer.className = firstImage.className;
      layer.alt = '';
      layer.setAttribute('aria-hidden', 'true');
      layer.decoding = 'async';
      if (sizes) layer.setAttribute('sizes', sizes);
      if (srcset) layer.setAttribute('srcset', srcset);
      layer.src = src;

      const fit = getComputedStyle(firstImage);
      const duration = reducedMotion() ? 0 : 200;
      Object.assign(layer.style, {
        position: 'absolute',
        inset: '0',
        zIndex: '1',
        width: '100%',
        height: '100%',
        objectFit: fit.objectFit || 'cover',
        objectPosition: fit.objectPosition || '50% 50%',
        opacity: '0',
        transition: 'opacity ' + duration + 'ms ease',
        pointerEvents: 'none',
      });
      holder.appendChild(layer);

      const reveal = () => {
        if (token !== fadeToken) {
          layer.remove();
          return;
        }

        requestAnimationFrame(() => {
          layer.style.opacity = '1';
        });

        window.setTimeout(() => {
          if (token !== fadeToken) {
            layer.remove();
            return;
          }

          // A foto principal passa a ser a da nova cor e a camada sai sem piscar.
          if (sizes) firstImage.setAttribute('sizes', sizes);
          if (srcset) firstImage.setAttribute('srcset', srcset);
          else firstImage.removeAttribute('srcset');
          firstImage.src = src;
          firstImage.alt = media.alt || firstImage.dataset.niviereeOriginalAlt || '';

          const decoded = firstImage.decode ? firstImage.decode().catch(() => {}) : Promise.resolve();
          decoded.then(() => layer.remove());
        }, duration + 30);
      };

      const ready = layer.decode ? layer.decode() : Promise.resolve();
      ready.then(reveal, reveal);
    };

    const restoreFirstImage = () => {
      if (!firstImage) return;

      const src = firstImage.dataset.niviereeOriginalSrc;
      const srcset = firstImage.dataset.niviereeOriginalSrcset;
      const alt = firstImage.dataset.niviereeOriginalAlt;

      if (src) firstImage.src = src;
      if (srcset) firstImage.setAttribute('srcset', srcset);
      else firstImage.removeAttribute('srcset');
      firstImage.alt = alt || '';
    };

    const syncQuickAddColor = (color) => {
      if (!quickPanel) return;

      quickPanel.dataset.selectedColor = color || '';

      variantChoices.forEach((choice) => {
        const choiceColor = choice.dataset.color || '';
        const matches = !choiceColor || !color || choiceColor === color;
        choice.hidden = !matches;
      });
    };

    const closeQuickAdd = () => {
      if (!quickPanel || !quickToggle) return;
      quickPanel.classList.remove('is-open');
      quickPanel.setAttribute('aria-hidden', 'true');
      quickToggle.setAttribute('aria-expanded', 'false');
    };

    const openQuickAdd = () => {
      if (!quickPanel || !quickToggle) return;

      document.querySelectorAll(CARD_SELECTOR + ' [data-nivieree-quick-panel].is-open').forEach((panel) => {
        if (panel !== quickPanel) {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
          panel.closest(CARD_SELECTOR)?.querySelector('[data-nivieree-quick-toggle]')?.setAttribute('aria-expanded', 'false');
        }
      });

      quickPanel.classList.add('is-open');
      quickPanel.setAttribute('aria-hidden', 'false');
      quickToggle.setAttribute('aria-expanded', 'true');
    };

    previousButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      step(-1);
    });

    nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      step(1);
    });

    dots.forEach((dot, index) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIndex(index);
      });
    });

    const isInsideFeaturedCarousel = Boolean(card.closest('.nivieree-featured-carousel'));
    /* O carrossel de produtos da Explore Collections não arrasta mais (só setinhas), então o gesto fica livre para a foto. */
    const isInsideExploreGrid = Boolean(card.closest('.nivieree-explore-products__grid'));
    const swipeEnabled = !isInsideFeaturedCarousel || isInsideExploreGrid;

    if (viewport && swipeEnabled && (slides.length > 1 || colorMode)) {
      // Gesto horizontal é da foto; rolagem vertical da página continua normal.
      viewport.style.setProperty('touch-action', 'pan-y', 'important');
      viewport.addEventListener('dragstart', (event) => event.preventDefault());

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, input, label')) return;
        tracking = true;
        didSwipe = false;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
      }, { passive: true });

      viewport.addEventListener('pointermove', (event) => {
        if (!tracking || event.pointerId !== pointerId || event.pointerType !== 'mouse') return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY) && !viewport.hasPointerCapture(pointerId)) {
          viewport.setPointerCapture(pointerId);
        }
      }, { passive: true });

      const finishSwipe = (event) => {
        if (!tracking || event.pointerId !== pointerId) return;
        tracking = false;

        if (viewport.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
        if (event.type === 'pointercancel') return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;

        if (Math.abs(deltaX) > 38 && Math.abs(deltaX) > Math.abs(deltaY)) {
          didSwipe = true;
          step(deltaX < 0 ? 1 : -1);
        }
      };

      viewport.addEventListener('pointerup', finishSwipe, { passive: true });
      viewport.addEventListener('pointercancel', finishSwipe, { passive: true });

      viewport.addEventListener('click', (event) => {
        if (!didSwipe) return;
        event.preventDefault();
        event.stopPropagation();
        didSwipe = false;
      }, true);
    }

    colorInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const color = getLabelTextForInput(input);
        syncQuickAddColor(color);

        if (input.dataset.variantId && variantInput) {
          variantInput.value = input.dataset.variantId;
        }

        const mediaJson = input.dataset.variantMedia;
        if (!mediaJson) return;

        let media;
        try {
          media = JSON.parse(mediaJson);
        } catch (_error) {
          return; // Keep the current media if metadata cannot be parsed.
        }

        if (colorMode) {
          fadeToMedia(media);
          return;
        }

        const mediaId = String(media.id || '');
        const matchingIndex = slides.findIndex((slide) => String(slide.dataset.mediaId || '') === mediaId);

        if (matchingIndex >= 0) {
          if (matchingIndex === 0) restoreFirstImage();
          setIndex(matchingIndex);
          return;
        }

        const preview = media.preview_image || media.previewImage;
        const previewUrl = preview?.src || preview?.url;

        if (firstImage && previewUrl) {
          firstImage.removeAttribute('srcset');
          firstImage.src = previewUrl;
          firstImage.alt = media.alt || firstImage.alt || '';
          setIndex(0);
        }
      });
    });

    const checkedColor = colorInputs.find((input) => input.checked);
    if (checkedColor) syncQuickAddColor(getLabelTextForInput(checkedColor));
    else syncQuickAddColor(quickPanel?.dataset.selectedColor || '');

    quickToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (quickPanel?.classList.contains('is-open')) closeQuickAdd();
      else openQuickAdd();
    });

    variantChoices.forEach((choice) => {
      choice.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (choice.disabled || !variantInput || !quickForm) return;

        variantInput.value = choice.dataset.variantId;
        closeQuickAdd();

        if (typeof quickForm.requestSubmit === 'function') quickForm.requestSubmit();
        else quickForm.submit();
      });
    });

    quickForm?.addEventListener('submit', () => {
      closeQuickAdd();
    });

    card.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && quickPanel?.classList.contains('is-open')) {
        closeQuickAdd();
        quickToggle?.focus();
      }
    });
  };

  const initAll = (root = document) => {
    root.querySelectorAll?.(CARD_SELECTOR).forEach(initCard);
    if (root.matches?.(CARD_SELECTOR)) initCard(root);
  };

  const start = () => {
    initAll(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) initAll(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('click', (event) => {
      document.querySelectorAll(CARD_SELECTOR + ' [data-nivieree-quick-panel].is-open').forEach((panel) => {
        const card = panel.closest(CARD_SELECTOR);

        if (card && !card.contains(event.target)) {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
          card.querySelector('[data-nivieree-quick-toggle]')?.setAttribute('aria-expanded', 'false');
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
